import { useEffect } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { safeLocalStorage } from '../utils/storage'
import { ConfigProvider } from '../contexts/ConfigContext'
import AnalyticsTracker from '../analytics/AnalyticsTracker'

const INTENDED_URL_KEY = 'intendedUrl'

const NON_REDIRECT_ROUTES = [
    '/',
    '/register',
    '/login',
    '/access-denied',
    '/workspaces',
    '/verify',
] as const

function storeIntendedUrl(path: string) {
    const isProtected = !NON_REDIRECT_ROUTES.includes(
        path as (typeof NON_REDIRECT_ROUTES)[number]
    )
    if (isProtected && !safeLocalStorage.getItem(INTENDED_URL_KEY)) {
        safeLocalStorage.setItem(INTENDED_URL_KEY, path)
    }
}

/**
 * Route guard. The backend decides via /auth/session.nextAction whether
 * the user can render app routes:
 *  - No session (auth or anon)     → /login
 *  - next_action === 'verify_email' → /verify
 *  - next_action === 'select_workspace' → /workspaces
 *  - Otherwise (anon, or auth with active tenant) → render child routes
 */
const PrivateRoutes = () => {
    const location = useLocation()
    const {
        isAuthenticated,
        isAnonymous,
        nextAction,
        user,
        loading: authContextLoading,
    } = useAuth()

    // Remember where the unauth user wanted to go, so login can return them.
    useEffect(() => {
        if (!isAuthenticated && !isAnonymous) {
            storeIntendedUrl(location.pathname + location.search)
        }
    }, [location.pathname, location.search, isAuthenticated, isAnonymous])

    if (authContextLoading) return <div />

    if (!isAuthenticated && !isAnonymous) {
        return <Navigate to="/login" replace />
    }

    if (isAuthenticated && nextAction === 'verify_email' && user) {
        return (
            <Navigate
                to={`/verify?email=${encodeURIComponent(user.email)}`}
                replace
            />
        )
    }

    if (nextAction === 'select_workspace') {
        return <Navigate to="/workspaces" replace />
    }

    return (
        <ConfigProvider>
            <AnalyticsTracker />
            <Outlet />
        </ConfigProvider>
    )
}

export default PrivateRoutes
